import axios from 'axios'
import { useApiClient } from '@/composables/api-client'
import type { ApiClientCallConfig } from '@/composables/api-client/types'
import useWorkspace from '@/store/workspace'
import {
    expiredSessionInterceptor,
    gatewayTimeoutInterceptor,
    misleadingHtmlResponseInterceptor,
} from '../helpers'
import type { WorkspaceType } from '@/types'

const envVars = import.meta.env
const testMode = envVars.MODE === 'test'

const baseUrl = (workspaceType?: WorkspaceType): string => {
    if (envVars.VITE_BUILDER_DEV_API_URL) {
        return envVars.VITE_BUILDER_DEV_API_URL
    }

    const url = workspaceType === 'sandbox'
        ? envVars.VITE_SANDBOX_BUILDER_API_URL
        : envVars.VITE_BUILDER_API_URL

    return url.replace(/\/+$/, '')
}

export const builderAxiosInstance = axios.create({
    withCredentials: true,
})

!testMode && builderAxiosInstance.interceptors.response.use(
    misleadingHtmlResponseInterceptor,
    async (error) => {
        return await expiredSessionInterceptor(error, builderAxiosInstance)
    },
)

!testMode && builderAxiosInstance.interceptors.response.use(
    (res) => res,
    async (error) => {
        return await gatewayTimeoutInterceptor(error)
    },
)

export function useBuilderApiClient<T, D = ANY>(config: ApiClientCallConfig<D>) {
    // when it was on top of the file it was breaking the tests
    const { currentWorkspaceType } = useWorkspace()

    const _config = {
        ...config,
        baseURL: baseUrl(currentWorkspaceType.value),
    }
    return useApiClient<T, D>(_config, builderAxiosInstance)
}
